import React from "react";

import { FLOW, planLabel } from "./flowCopy";
import { Eyebrow, ingestedAt } from "./PlanCard";
import { planFlow } from "./planFlow";
import { PlanNav, PlanTab, TONE_CLASS, shortDate } from "./shared";
import type { AccountPlanOverview } from "../../Types/AccountPlan";

interface Props {
  data: AccountPlanOverview;
  nav: PlanNav;
}

/** The step an AE/CSM should be on (mock v4 `currentStepFor`). */
export const currentStep = (data: AccountPlanOverview): PlanTab => {
  // No readable version yet (none uploaded, or the first still being read).
  if (!data.current_file_id) return "plan";
  // Read, waiting on Confirm plan (Phase 2).
  if (!planFlow(data).currentConfirmed) return "viewer";
  return data.current_review?.published ? "publish" : "sponsor";
};

/**
 * What the account's sponsors read right now. Follows the version they are
 * actually shown (overview `sponsor_file_id`), which can be an older published
 * version while a newer upload waits for its draft; falls back to the current
 * version's review when the overview doesn't say.
 */
export const sponsorSees = (data: AccountPlanOverview): string => {
  if (data.sponsor_file_id === undefined) {
    const status = data.current_review?.status ?? "none";
    if (status === "none") return "Nothing yet";
    return status === "published"
      ? "Published briefing"
      : "Draft · under review";
  }
  if (!data.sponsor_file_id) return "Nothing yet";
  const label =
    data.sponsor_view_state === "published"
      ? "Published briefing"
      : "Draft · under review";
  if (data.sponsor_file_id === data.current_file_id) return label;
  const version = data.files.find(
    f => f.id === data.sponsor_file_id
  )?.plan_version;
  return version != null ? `${label} (v${version})` : label;
};

/**
 * "Account plan status" details on the Upload step (was "Pipeline status").
 * The chip comes from the same derivation as the header's status strip, so the
 * two never disagree. No button: the stepper and each tab's single next-step
 * card are the one place to act (STP-12).
 */
const PlanPipelineCard: React.FC<Props> = ({ data }) => {
  const review = data.current_review;
  const current = data.files.find(f => f.id === data.current_file_id) || null;
  const flow = planFlow(data);
  const status = review?.status ?? "none";

  // An upload not yet a version (being read, or waiting on Confirm plan) is never
  // "Awaiting upload" (Sushant, 2026-11-03): say where it is.
  const pending =
    flow.reading || (flow.toConfirm !== current ? flow.toConfirm : null);
  // One state, one name (UI tour A item 16): an upload waiting on the AE's Confirm
  // plan reads "waiting to confirm" here, as in the version list, flagged or not.
  const waitingToConfirm = FLOW.waitingForReview.toLowerCase();
  const pendingText = pending
    ? `${planLabel(pending)} · ${
        pending.status === "processing" ? "being read" : waitingToConfirm
      }`
    : null;

  const rows: [string, string][] = [
    [
      "Plan",
      current
        ? `${planLabel(current)} · ${
            flow.currentConfirmed
              ? shortDate(ingestedAt(current))
              : waitingToConfirm
          }`
        : pendingText || "Awaiting upload",
    ],
    ...(current && pendingText
      ? ([["New upload", pendingText.replace(/^New upload · /, "")]] as [
          string,
          string,
        ][])
      : []),
    // A published version's briefing is no longer a draft (STP-13).
    review && flow.currentPublished && flow.published
      ? [
          "Published briefing",
          `v${flow.published.planVersion} · ${shortDate(flow.published.at)}`,
        ]
      : [
          "Draft briefing",
          review
            ? `from ${planLabel(review, "the new upload")} · ${shortDate(review.generated_at)}`
            : current
              ? flow.currentConfirmed
                ? flow.briefing === "stalled"
                  ? "Taking longer than usual"
                  : flow.briefing === "writing"
                    ? "Being written"
                    : "Not written"
                : "Written after you confirm the plan"
              : "—",
        ],
    [
      "Verified",
      review && status !== "none"
        ? `AE ${review.progress.AE.verified}/${review.progress.AE.owned} · CSM ${review.progress.CSM.verified}/${review.progress.CSM.owned}`
        : "—",
    ],
    ["Sponsor sees", sponsorSees(data)],
  ];

  return (
    <section
      data-testid="plan-status-card"
      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <Eyebrow>Status</Eyebrow>
      <span
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${TONE_CLASS[flow.chip.tone]}`}
      >
        {flow.chip.label}
      </span>
      <p className="mt-1.5 text-xs text-gray-500">{flow.line}</p>
      <dl className="mt-3 space-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3">
            <dt className="text-gray-500">{k}</dt>
            <dd className="text-right font-medium text-gray-800">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default PlanPipelineCard;
